import { NextFetchEvent, NextRequest, NextResponse } from "next/server"
const protected_routes = [
  "/setup",
  "/notes",
  "/dashboard",
  "/app/stats",
  "/app/subscribers",
  "/subscribers",
  "/settings/account/account",
  "/settings/account/author",
  "/settings/account/social",
  "/settings/publication/blog",
  `/settings/theme`,
  "/settings/publication/notifications",
  "/settings/publication/billing",
  "/settings/publication/communities",
]

/**
 * For custom domains, there are some routes that we don't want to rewrite to the custom domain.
 * This is becasue we want to allow users to access RSS feeds on paragraph.com, NOT their custom domains.
 */
const no_custom_domain_rewrites = [
  // Include common RSS routes here, so we don't rewrite to custom domains.
  "/feed",
  "/rss",
  "/robots.txt",
  "/apple-touch-icon.png",
]

/**
 * These are guest routes.
 * If the user is logged in, they should be redirected to /notes.
 *
 * And, for custom domains, we do NOT rewrite to the user's custom domain if one of these routes is detected.
 *
 * This lets readers register and use forgot password functionality on their own custom domain.
 */
const guest_routes = ["/register", "/login", "/forgot-password"]

const DOMAINS = [
  "papyrus.so",
  "paragraph.com",
  "paragraph.xyz",
  "papyrus.xyz",
  "papyrus.dev",
  "vercel.app",
  "localhost:3001",
  "localhost:3000",

  // Local tunnel; for exposing local dev servers to internet.
  "loca.lt",
  "ngrok-free.app",
]

if (process.env.NEXT_PUBLIC_FORCE_DOMAIN) {
  console.log("Forcing domain", process.env.NEXT_PUBLIC_FORCE_DOMAIN)

  const noHttp = process.env.NEXT_PUBLIC_FORCE_DOMAIN.replace(
    /^https?:\/\//,
    ""
  )
  DOMAINS.push(noHttp)
}

export const config = {
  matcher: [
    /*
     * Match all request paths except for the ones starting with:
     * - api (API routes)
     * - static (static files)
     * - favicon.ico (favicon file)
     * Need to exclude API routes
     * due to https://github.com/vercel/next.js/issues/39262.
     *
     * And ignore ones ending in images or other static assets.
     *
     * NOTE: This causes issues for custom domains, so don't be too lax with what we include here!
     *
     * See: https://linear.app/paragraph-xyz/issue/PAR-3337/fred-wilsons-post-404ing
     *
     */

    "/((?!api|js|_vercel|static|site\\.webmanifest|favicon\\.ico|_next|\\.gz|\\.js|\\.png|\\.json|\\.svg|\\.php|\\.xml).*)",
  ],
}

export const isCustomDomain = (hostname: string | null): boolean => {
  return (
    (hostname && !DOMAINS.some((domain) => hostname.endsWith(domain))) || false
  )
}

/**
 * Checks that the privy-token used for 1 hour access validation is present and not expired.
 */
const hasValidPrivyToken = (req: NextRequest): boolean => {
  // Check if we have privy-token and it's not expired
  if (
    req.cookies.has("privy-token") &&
    req.cookies.get("privy-token")?.value !== ""
  ) {
    const tokenValue = req.cookies.get("privy-token")?.value

    if (tokenValue) {
      try {
        // Decode Privy token payload (without verification, just to check expiration)
        const payload = JSON.parse(atob(tokenValue.split(".")[1]))
        const now = Math.floor(Date.now() / 1000)

        return payload.exp && payload.exp > now
      } catch (error) {
        console.error("[middleware] Failed to decode Privy token", error)
        return false
      }
    }
  }

  return false
}

const isGuestRoute = (req: NextRequest): boolean => {
  const hostname = req.headers.get("host")

  const pathName = req.nextUrl.pathname

  // If we're visiting landing page, consider it a guest route
  if (pathName === "/" && !isCustomDomain(hostname)) {
    return true
  }

  const startsWithGuestRoute = guest_routes.some((route) =>
    /**
     *
     * Note, we NEED to do starts with, since there could be legit usecases (eg /subscribe/login) which we want rewritten.
     */

    req.nextUrl.pathname.startsWith(route)
  )

  return startsWithGuestRoute
}

export default async function middleware(req: NextRequest, ev: NextFetchEvent) {
  const url = req.nextUrl.clone()
  // Get hostname (e.g. vercel.com, test.vercel.app, etc.)
  const hostname = req.headers.get("host")

  const hasValidPrivyAccessTokenCookie = hasValidPrivyToken(req)

  // To maintain backwards compatibility with users who are still logged in using pre-Privy tokens.
  const hasJwt = req.cookies.has("jwt") && req.cookies.get("jwt")?.value !== ""

  if (url.searchParams.get("frame") && !req.cookies.has("frame")) {
    url.searchParams.delete("frame")
    const response = NextResponse.redirect(url, 301)

    response.cookies.set("frame", "true", {
      // We REQUIRE these, or else Farcaster will not properly send the cookies.
      sameSite: "none",
      secure: true,
    })
    return response
  }

  const inFrame =
    req.cookies.has("frame") && req.cookies.get("frame")?.value !== ""

  // If not on refresh route and no valid Privy access token but has privy-session cookie so we should try to refresh the Privy token.
  //
  // This only happens on the server-side. On the client-side, the access token gets refreshed via lib/helper.ts.
  if (
    !req.url.includes("/refresh") &&
    !hasValidPrivyAccessTokenCookie &&
    !hasJwt &&
    req.cookies.has("privy-session")
  ) {
    url.pathname = "/refresh"
    // Preserve the redirect URL so we can redirect back after refresh or login
    url.searchParams.append("redirect", req.nextUrl.pathname)
    return NextResponse.redirect(url)
  }

  // If the user is logged out, and trying to access a protected route,
  // redirect them to the login page.
  if (
    protected_routes.includes(req.nextUrl.pathname) &&
    !hasValidPrivyAccessTokenCookie &&
    !hasJwt
  ) {
    url.pathname = "/"
    url.searchParams.append("login", "true")
    // Preserve the redirect URL so we can redirect back after login or token refresh
    url.searchParams.append("redirect", req.nextUrl.pathname)
    return NextResponse.redirect(url)
  }

  const startsWithGuestRoute = isGuestRoute(req)

  if (startsWithGuestRoute && (hasValidPrivyAccessTokenCookie || hasJwt)) {
    url.pathname = "/home"
    console.log(
      "User visiting " +
        req.nextUrl.pathname +
        " but logged in, so redirecting to home"
    )
    return NextResponse.redirect(url)
  }

  // If this is a custom domain and they're logged in, and they're trying to access a protected route, let's redirect user to the main paragraph.com domain.
  if (
    isCustomDomain(hostname) &&
    // If it's not already rewritten
    !url.pathname.startsWith(`/@${hostname}`) &&
    protected_routes.includes(req.nextUrl.pathname) &&
    (hasValidPrivyAccessTokenCookie || hasJwt)
  ) {
    console.log(
      "Custom domain accessing protected route, so redirecting to paragraph.com"
    )
    return NextResponse.redirect("https://paragraph.com" + url.pathname)
  }

  const pathnameIncludesNoRewriteCustomDomainRoute =
    no_custom_domain_rewrites.some((route) => url.pathname.includes(route))

  const alreadyRewritten = url.pathname.startsWith(`/@${hostname}`)

  if (isCustomDomain(hostname)) {
    // For RSS feeds on custom domains, redirect to the RSS feed API
    if (pathnameIncludesNoRewriteCustomDomainRoute) {
      switch (url.pathname) {
        case "/feed":
        case "/rss": {
          return NextResponse.redirect(
            "https://api.paragraph.com/blogs/rss/@" + hostname,
            308
          )
        }
        case "/robots.txt": {
          return NextResponse.redirect("https://paragraph.com/robots.txt", 308)
        }
        default:
          break
      }
    }

    if (alreadyRewritten) {
      // If already rewritten, redirect to the root of the custom domain
      console.log(`Redirecting ${hostname} to root of custom domain`)
      // Strip out the /@{hostname} from the path
      let newPathname = url.pathname.replace(`/@${hostname}`, "")

      // Handle the case where the newPathname becomes empty, which should default to '/'
      if (newPathname === "") newPathname = "/"

      console.log(`Redirecting to ${hostname} with path ${newPathname}`)
      return NextResponse.redirect(`https://${hostname}${newPathname}`)
      //
    } else if (
      // If it's not already rewritten
      !url.pathname.startsWith(`/@${hostname}`) &&
      // Skip rewriting these pages for custom domains,
      // since we want eg cma.xyz/register to work.
      !startsWithGuestRoute &&
      // We do NOT want to rewrite custom domains if the user is accessing it via frame.
      //
      // This is because we want to preserve the Paragraph domain in the URL, since
      // Farcaster relies on the frame to be served from "paragraph.com"
      !inFrame
    ) {
      // If this is a custom domain, immediately rewrite since
      // the rest of the logic relies on treating it like a normal
      // domain.
      url.pathname = `/@${hostname}` + url.pathname
      console.log(
        `Custom domain ${hostname} accessing normal route, so rewriting to ${url.pathname}`
      )
    }
  }

  // [ "/careers" ] (random pages)
  // [ "@blogname",  "post-slug" ] (blog posts)
  // [ "@blogname" ] (blog home page)
  const parts = url.pathname?.split("/")?.filter((p) => p)

  // Lowercase the blogname so we handle this consistently (eg with revalidation,
  // https://github.com/paragraph-xyz/monorepo/issues/1149).
  const blog = parts?.[0]?.toLowerCase()

  // if the user is visiting a page that isn't a blog page (eg /careers),
  // redirect them normally.
  if (!blog?.startsWith("@")) {
    return NextResponse.next()
  }

  // If the user is visiting a blog with a capital letter,
  // redirect them to the lowercase version
  if (blog !== parts?.[0]) {
    url.pathname = `/${blog}` + url.pathname?.slice(parts?.[0].length + 1)
    console.log("Redirecting to lowercase blogname", blog, parts, url.pathname)

    return NextResponse.redirect(url, 308)
  }

  console.log("Redirecting to normal route", blog, parts, url.pathname)

  // At this point, we're either on the blog home page, or a blog post page.
  // No further conditionals are needed, but we still need to rewrite the URL,
  // since the user could be visiting a custom domain and above we appended the custom domain
  // to the URL (for the blogpost logic to work as intended).
  return NextResponse.rewrite(url)
}
